import { PCNode } from '../../MineModel/Nodes';

interface Coords {
  x: number;
  y: number;
  z: number;
}

// Pre-computed 3x3x3 neighbour offsets (27 cells)
const NEIGHBOUR_OFFSETS: [number, number, number][] = [
  [-1, -1, -1], [-1, -1, 0], [-1, -1, 1],
  [-1, 0, -1], [-1, 0, 0], [-1, 0, 1],
  [-1, 1, -1], [-1, 1, 0], [-1, 1, 1],
  [0, -1, -1], [0, -1, 0], [0, -1, 1],
  [0, 0, -1], [0, 0, 0], [0, 0, 1],
  [0, 1, -1], [0, 1, 0], [0, 1, 1],
  [1, -1, -1], [1, -1, 0], [1, -1, 1],
  [1, 0, -1], [1, 0, 0], [1, 0, 1],
  [1, 1, -1], [1, 1, 0], [1, 1, 1],
];

const cellKey = (cx: number, cy: number, cz: number): string => `${cx},${cy},${cz}`;

// Cell size must be >= the largest tolerance used in queries
export class SpatialHash<T extends { coords: Coords }> {
  private cellSize: number;

  private cells = new Map<string, T[]>();

  constructor(cellSize: number = 1.0) {
    this.cellSize = cellSize;
  }

  private quantize(v: number): number {
    return Math.floor(v / this.cellSize);
  }

  insert(item: T): void {
    const { x, y, z } = item.coords;
    const cx = this.quantize(x);
    const cy = this.quantize(y);
    const cz = this.quantize(z);
    const key = cellKey(cx, cy, cz);
    const bucket = this.cells.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      this.cells.set(key, [item]);
    }
  }

  findNearest(x: number, y: number, z: number, tolerance: number): T | null {
    const cx = this.quantize(x);
    const cy = this.quantize(y);
    const cz = this.quantize(z);
    const tolSq = tolerance * tolerance;
    let best: T | null = null;
    let bestDistSq = tolSq;

    NEIGHBOUR_OFFSETS.forEach(([dx, dy, dz]) => {
      const bucket = this.cells.get(cellKey(cx + dx, cy + dy, cz + dz));
      if (!bucket) return;
      bucket.forEach((item) => {
        const ddx = item.coords.x - x;
        const ddy = item.coords.y - y;
        const ddz = item.coords.z - z;
        const distSq = ddx * ddx + ddy * ddy + ddz * ddz;
        if (distSq <= bestDistSq) {
          bestDistSq = distSq;
          best = item;
        }
      });
    });

    return best;
  }

  queryInBounds(minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number): T[] {
    const cx0 = this.quantize(minX) - 1;
    const cy0 = this.quantize(minY) - 1;
    const cz0 = this.quantize(minZ) - 1;
    const cx1 = this.quantize(maxX) + 1;
    const cy1 = this.quantize(maxY) + 1;
    const cz1 = this.quantize(maxZ) + 1;
    const results: T[] = [];
    const seen = new Set<T>();

    for (let ix = cx0; ix <= cx1; ix += 1) {
      for (let iy = cy0; iy <= cy1; iy += 1) {
        for (let iz = cz0; iz <= cz1; iz += 1) {
          const bucket = this.cells.get(cellKey(ix, iy, iz));
          if (bucket) {
            bucket.forEach((item) => {
              if (!seen.has(item)
                && item.coords.x >= minX && item.coords.x <= maxX
                && item.coords.y >= minY && item.coords.y <= maxY
                && item.coords.z >= minZ && item.coords.z <= maxZ) {
                seen.add(item);
                results.push(item);
              }
            });
          }
        }
      }
    }

    return results;
  }
}

export const buildNodeSpatialHash = (nodes: PCNode[], cellSize: number = 1.0): SpatialHash<PCNode> => {
  const hash = new SpatialHash<PCNode>(cellSize);
  nodes.forEach((node) => hash.insert(node));
  return hash;
};
